import { z } from 'zod';
import type { KpiDirectionName } from './kpis';

// ---------------------------------------------------------------------------------------------
// Histórico de decisões: o indicador melhorou depois do prazo? (PRD §19 e pergunta 13; DEC-051)

export const DECISION_OUTCOMES = [
  'IMPROVED',
  'WORSENED',
  'STABLE',
  'WAITING',
  'NO_DATA_AFTER',
  'NO_DUE_DATE',
  'NO_BASELINE',
  'NOT_COMPARABLE',
] as const;
export type DecisionOutcomeName = (typeof DECISION_OUTCOMES)[number];
export const DECISION_OUTCOME_LABELS: Record<DecisionOutcomeName, string> = {
  IMPROVED: 'Melhorou',
  WORSENED: 'Piorou',
  STABLE: 'Estável',
  WAITING: 'Aguardando o prazo',
  NO_DATA_AFTER: 'Sem dado depois do prazo',
  NO_DUE_DATE: 'Sem prazo',
  NO_BASELINE: 'Sem retrato anterior',
  NOT_COMPARABLE: 'Sem comparação',
};

/** Diferença de performance (pontos percentuais) até a qual o indicador fica "estável". */
export const OUTCOME_STABLE_POINTS = 1;
/** Sem performance: diferença relativa do resultado até a qual fica "estável". */
export const OUTCOME_STABLE_RATIO = 0.01;

/** Um mês da série: performance (% da meta) e resultado. */
export type OutcomePoint = { period: string; performance: number | null; result: number | null };

export const outcomePointSchema = z.object({
  /** Mês ("AAAA-MM"). */
  period: z.string(),
  performance: z.number().nullable(),
  result: z.number().nullable(),
});
export const decisionOutcomeSchema = z.object({
  status: z.enum(DECISION_OUTCOMES),
  before: outcomePointSchema.nullable(),
  after: outcomePointSchema.nullable(),
});
export type DecisionOutcome = z.infer<typeof decisionOutcomeSchema>;

/** Último dia do mês "AAAA-MM" (AAAA-MM-DD). */
function monthEnd(period: string): string {
  const [y, m] = period.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${period}-${String(last).padStart(2, '0')}`;
}

const hasData = (p: OutcomePoint) => p.performance !== null || p.result !== null;

/**
 * O indicador melhorou depois do prazo (DEC-051)? Antes = o retrato do item quando a pauta foi montada; depois = o
 * último mês com dado, posterior ao do retrato, que termina no prazo ou depois dele (até hoje). Compara a
 * performance (que já considera a polaridade): até 1 ponto = estável. Sem performance dos dois lados, compara o
 * resultado pela polaridade (maior ou menor é melhor) com tolerância de 1%; faixa e fórmula ficam sem comparação.
 */
export function decisionOutcome(input: {
  dueDate: string | null;
  before: OutcomePoint | null;
  /** Meses com dado do assunto (qualquer ordem). */
  series: OutcomePoint[];
  /** Polaridade do KPI; o objetivo é sempre "maior é melhor" (performance). */
  direction: KpiDirectionName;
  /** Hoje (AAAA-MM-DD). */
  today: string;
}): DecisionOutcome {
  const { dueDate, before, today } = input;
  if (!before || !hasData(before)) return { status: 'NO_BASELINE', before: null, after: null };
  if (!dueDate) return { status: 'NO_DUE_DATE', before, after: null };
  if (today < dueDate) return { status: 'WAITING', before, after: null };
  const currentMonth = today.slice(0, 7);
  const after =
    input.series
      .filter(
        (p) =>
          hasData(p) && p.period > before.period && p.period <= currentMonth && monthEnd(p.period) >= dueDate,
      )
      .sort((a, b) => a.period.localeCompare(b.period))
      .at(-1) ?? null;
  if (!after) return { status: 'NO_DATA_AFTER', before, after: null };

  if (before.performance !== null && after.performance !== null) {
    const diff = after.performance - before.performance;
    const status = Math.abs(diff) <= OUTCOME_STABLE_POINTS ? 'STABLE' : diff > 0 ? 'IMPROVED' : 'WORSENED';
    return { status, before, after };
  }
  const sign = input.direction === 'HIGHER_BETTER' ? 1 : input.direction === 'LOWER_BETTER' ? -1 : 0;
  if (sign === 0 || before.result === null || after.result === null) {
    return { status: 'NOT_COMPARABLE', before, after };
  }
  const diff = (after.result - before.result) * sign;
  const tolerance = Math.abs(before.result) * OUTCOME_STABLE_RATIO;
  const status = Math.abs(diff) <= tolerance ? 'STABLE' : diff > 0 ? 'IMPROVED' : 'WORSENED';
  return { status, before, after };
}
